import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { approveReprintRequest, getReprintRequests } from "../../services";
import {
  ReprintRequestRow,
  ReprintRequestStatusFilter,
} from "../../types/reprintRequest.type";
import { formatDate } from "../../utils/date";
import ReprintRequestPagination from "./Pagination";
import ReprintRequestSkeleton from "./Skeleton";
import RejectDialog from "./RejectDialog";
import {
  STATUS_CHIP,
  filterRowsInPage,
  getRequestedCopiesLabel,
  isReviewable,
  roleLabel,
  transactionTypeLabel,
} from "./data";

interface ReprintRequestTableProps {
  search: string;
  status: string;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  triggerFetch: number;
  onRefresh: () => void;
  updatedAt: string;
  setUpdatedAt: Dispatch<SetStateAction<string>>;
}

const COLUMN_COUNT = 8;
const LIMIT = 10;

export default function ReprintRequestTable({
  search,
  status,
  dateFrom,
  dateTo,
  triggerFetch,
  onRefresh,
  updatedAt,
  setUpdatedAt,
}: ReprintRequestTableProps) {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rows, setRows] = useState<ReprintRequestRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // uuid of the row whose approve call is in flight — guards against a
  // double-tap firing two approvals (the second would 409).
  const [approvingUuid, setApprovingUuid] = useState<string | null>(null);
  const [rejectRequest, setRejectRequest] = useState<ReprintRequestRow | null>(
    null,
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getReprintRequests({
        page,
        limit,
        status: status as ReprintRequestStatusFilter,
      });

      setRows((data.rows ?? []) as ReprintRequestRow[]);
      setTotalCount(data.count ?? 0);
      setUpdatedAt(formatDate(new Date().toISOString(), "DD/MM/YYYY HH:mm"));
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, setUpdatedAt]);

  useEffect(() => {
    fetchData();
  }, [triggerFetch, fetchData]);

  // Reset to the first page whenever the server-side filter changes.
  useEffect(() => {
    setPage(1);
  }, [status]);

  const handleApprove = async (row: ReprintRequestRow) => {
    setApprovingUuid(row.uuid);
    try {
      await approveReprintRequest(row.uuid);
      onRefresh();
    } finally {
      setApprovingUuid(null);
    }
  };

  // Page-scoped only — the BE has no free-text/date filter (see data.ts).
  const visibleRows = filterRowsInPage(rows, search, dateFrom, dateTo);

  const headerCellSx = {
    borderRight: (theme: import("@mui/material/styles").Theme) =>
      `1px solid ${theme.palette.draft.main}`,
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              <TableCell sx={headerCellSx}>เลขที่บิล</TableCell>
              <TableCell sx={headerCellSx}>ลูกค้า</TableCell>
              <TableCell sx={headerCellSx}>ผู้ขอ</TableCell>
              <TableCell sx={headerCellSx}>ชุดที่ขอ</TableCell>
              <TableCell sx={headerCellSx}>เหตุผล</TableCell>
              <TableCell sx={headerCellSx}>เวลาที่ขอ</TableCell>
              <TableCell sx={headerCellSx} align="center">
                สถานะ
              </TableCell>
              <TableCell align="center">จัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <ReprintRequestSkeleton columns={COLUMN_COUNT} rows={limit} />
            )}

            {!loading && visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูลตามเงื่อนไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              visibleRows.map((row, index) => {
                const chip = STATUS_CHIP[row.status];
                const reviewable = isReviewable(row);
                const approving = approvingUuid === row.uuid;

                return (
                  <TableRow
                    key={row.uuid}
                    sx={{
                      bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                      "& td, & th": { padding: "8px" },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.transaction_no || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {transactionTypeLabel(row.transaction_type)}
                        {row.route_code ? ` · ${row.route_code}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.customer_name || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.customer_code || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.requested_by_name || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {roleLabel(row.requested_by_role)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getRequestedCopiesLabel(row.requested_copies)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Tooltip title={row.reason || ""}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.reason || "-"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.created_at
                          ? formatDate(row.created_at, "DD/MM/YYYY HH:mm")
                          : "-"}
                      </Typography>
                      {row.reviewed_at && (
                        <Typography variant="caption" color="text.secondary">
                          ตรวจ {formatDate(row.reviewed_at, "DD/MM/YYYY HH:mm")}
                          {row.reviewed_by_name ? ` · ${row.reviewed_by_name}` : ""}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={chip.label} color={chip.color} />
                      {row.status === "REJECTED" && row.review_note && (
                        <Tooltip title={row.review_note}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              maxWidth: 140,
                              mx: "auto",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.review_note}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        {/* Non-PENDING rows are terminal — the BE 409s on any
                            further review, so the actions are hidden entirely. */}
                        {reviewable ? (
                          <>
                            <Tooltip title="อนุมัติ (ให้สิทธิ์ 3 ชม.)">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<CheckCircleOutlineIcon />}
                                  loading={approving}
                                  disabled={Boolean(approvingUuid)}
                                  onClick={() => handleApprove(row)}
                                >
                                  อนุมัติ
                                </Button>
                              </span>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<HighlightOffIcon />}
                              disabled={Boolean(approvingUuid)}
                              onClick={() => setRejectRequest(row)}
                            >
                              ปฏิเสธ
                            </Button>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            ดำเนินการแล้ว
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        pt={1.5}
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          ทั้งหมด {totalCount} รายการ
        </Typography>
        <Typography variant="caption" color="text.secondary">
          อัปเดตล่าสุด {updatedAt || "-"}
        </Typography>
      </Box>

      <ReprintRequestPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />

      <RejectDialog
        open={Boolean(rejectRequest)}
        request={rejectRequest}
        onClose={() => setRejectRequest(null)}
        onRejected={onRefresh}
      />
    </Paper>
  );
}
